export const PASSING_ACCURACY = 90;

export function requiredWpm(goalWpm) {
  return Math.ceil((goalWpm || 0) * 0.85);
}

export function requiredEpm(lesson) {
  // Browser display target only. Production remains server-authoritative.
  // The established keypad model uses EPM; the curriculum's goal value is converted to an EPM target.
  return Math.max(1, Math.ceil((lesson?.goalWpm || 1) * 5 * 0.9));
}

export function calculateEpm(text, elapsedMinutes) {
  if (!elapsedMinutes || elapsedMinutes <= 0) return 0;
  return Math.max(0, Math.round(text.length / elapsedMinutes));
}

export function calculateWpm(text, elapsedMinutes) {
  if (!elapsedMinutes || elapsedMinutes <= 0) return 0;
  return Math.max(0, Math.round(text.length / 5 / elapsedMinutes));
}

function classifyAlignedError(expected, actual, operation) {
  if (expected === '\n' || actual === '\n') return 'hardReturn';
  if (expected === ' ' || actual === ' ' || expected === '\t' || actual === '\t') return 'spacing';
  if (operation === 'delete') return 'omitted';
  if (operation === 'insert') return 'extra';
  if (expected && actual && expected.toLowerCase() === actual.toLowerCase()) return 'capitalization';
  if (/\d/.test(expected || '') || /\d/.test(actual || '')) return 'numberEntry';
  if (/[^A-Za-z0-9\s]/.test(expected || '') || /[^A-Za-z0-9\s]/.test(actual || '')) return 'symbol';
  return 'substitution';
}

function alignCharacters(typed, target) {
  const a = String(typed ?? '').replaceAll('\r\n', '\n').replaceAll('\r', '\n');
  const b = String(target ?? '').replaceAll('\r\n', '\n').replaceAll('\r', '\n');
  const n = a.length;
  const m = b.length;
  const dp = Array.from({ length: n + 1 }, () => new Uint16Array(m + 1));
  for (let i = 0; i <= n; i += 1) dp[i][0] = i;
  for (let j = 0; j <= m; j += 1) dp[0][j] = j;
  for (let i = 1; i <= n; i += 1) {
    for (let j = 1; j <= m; j += 1) {
      const substitution = dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1);
      const insertion = dp[i - 1][j] + 1;
      const deletion = dp[i][j - 1] + 1;
      dp[i][j] = Math.min(substitution, insertion, deletion);
    }
  }

  const operations = [];
  let i = n;
  let j = m;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && a[i - 1] === b[j - 1] && dp[i][j] === dp[i - 1][j - 1]) {
      operations.push({ op: 'match', expected: b[j - 1], actual: a[i - 1], expectedIndex: j - 1, typedIndex: i - 1 });
      i -= 1; j -= 1; continue;
    }
    const current = dp[i][j];
    const canSubstitute = i > 0 && j > 0 && current === dp[i - 1][j - 1] + 1;
    const canDelete = j > 0 && current === dp[i][j - 1] + 1;
    const canInsert = i > 0 && current === dp[i - 1][j] + 1;
    // Prefer a direct substitution when it is truly one wrong keystroke. Otherwise
    // prefer deletion/insertion so one missed or added character realigns the copy.
    if (canSubstitute && !(canDelete || canInsert)) {
      operations.push({ op: 'substitute', expected: b[j - 1], actual: a[i - 1], expectedIndex: j - 1, typedIndex: i - 1 });
      i -= 1; j -= 1; continue;
    }
    if (canDelete) {
      operations.push({ op: 'delete', expected: b[j - 1], actual: '', expectedIndex: j - 1, typedIndex: i });
      j -= 1; continue;
    }
    if (canInsert) {
      operations.push({ op: 'insert', expected: '', actual: a[i - 1], expectedIndex: j, typedIndex: i - 1 });
      i -= 1; continue;
    }
    if (canSubstitute) {
      operations.push({ op: 'substitute', expected: b[j - 1], actual: a[i - 1], expectedIndex: j - 1, typedIndex: i - 1 });
      i -= 1; j -= 1; continue;
    }
    // Defensive fallback; normally unreachable.
    if (j > 0) { operations.push({ op: 'delete', expected: b[j - 1], actual: '', expectedIndex: j - 1, typedIndex: i }); j -= 1; }
    else if (i > 0) { operations.push({ op: 'insert', expected: '', actual: a[i - 1], expectedIndex: j, typedIndex: i - 1 }); i -= 1; }
  }
  operations.reverse();
  return { typed: a, target: b, operations, editDistance: dp[n][m] };
}

export function analyzeCopy(typed = '', target = '') {
  const { typed: a, target: b, operations, editDistance } = alignCharacters(typed, target);
  const errors = [];
  const errorCounts = { omitted: 0, extra: 0, substitution: 0, spacing: 0, capitalization: 0, symbol: 0, hardReturn: 0, numberEntry: 0 };
  let matched = 0;
  for (const item of operations) {
    if (item.op === 'match') { matched += 1; continue; }
    const type = classifyAlignedError(item.expected, item.actual, item.op);
    errorCounts[type] = (errorCounts[type] || 0) + 1;
    errors.push({
      position: (item.expectedIndex ?? item.typedIndex ?? 0) + 1,
      expected: item.expected || '∅',
      actual: item.actual || '∅',
      type: type.replace(/([A-Z])/g, ' $1').toLowerCase(),
      operation: item.op,
    });
  }
  const denominator = Math.max(a.length, b.length, 1);
  const accuracy = Math.max(0, Math.round(((denominator - editDistance) / denominator) * 100));
  return { accuracy, errors, errorCounts, correct: matched, editDistance };
}
function clampScore(value) {
  return Math.max(0, Math.min(100, Math.round(value)));
}

function sentenceAccuracyScore(errorCounts) {
  const penalty =
    (errorCounts.capitalization || 0) * 3 +
    (errorCounts.spacing || 0) * 2 +
    (errorCounts.symbol || 0) * 2 +
    (errorCounts.substitution || 0) * 2 +
    (errorCounts.omitted || 0) * 2;
  return clampScore(100 - penalty);
}

function copyControlScore(errorCounts, completionPercent) {
  const errorPenalty =
    (errorCounts.omitted || 0) * 4 +
    (errorCounts.extra || 0) * 3 +
    (errorCounts.spacing || 0) * 3 +
    (errorCounts.capitalization || 0) * 3 +
    (errorCounts.symbol || 0) * 3 +
    (errorCounts.numberEntry || 0) * 3 +
    (errorCounts.hardReturn || 0) * 5;
  const completionPenalty = completionPercent < 95 ? (95 - completionPercent) * 1.5 : 0;
  return clampScore(100 - errorPenalty - completionPenalty);
}

function workplaceFormattingScore(errorCounts) {
  const penalty =
    (errorCounts.spacing || 0) * 4 +
    (errorCounts.capitalization || 0) * 3 +
    (errorCounts.symbol || 0) * 2 +
    (errorCounts.hardReturn || 0) * 5;
  return clampScore(100 - penalty);
}

function businessReadinessScore({ accuracy, wpm, requiredSpeed, completion, errorCounts }) {
  const speedScore = Math.min(100, Math.round((wpm / Math.max(requiredSpeed || 1, 1)) * 100));
  const formattingScore = workplaceFormattingScore(errorCounts);
  return clampScore((accuracy * 0.35) + (speedScore * 0.25) + (completion * 0.2) + (formattingScore * 0.2));
}

export function scoreAttempt({ lesson, accuracy, wpm, epm, completion, errorCounts = {} }) {
  const isKeypadModule = lesson.module === 5;
  const requiredSpeed = isKeypadModule ? requiredEpm(lesson) : requiredWpm(lesson.goalWpm);
  const speedMetric = isKeypadModule ? epm : wpm;
  const speedScore = Math.min(100, Math.round((speedMetric / Math.max(requiredSpeed, 1)) * 100));

  if (lesson.module === 6) {
    const paragraphCompletion = clampScore(completion);
    const sentenceAccuracy = sentenceAccuracyScore(errorCounts);
    const control = copyControlScore(errorCounts, completion);
    const numbersSymbols = clampScore(100 - ((errorCounts.numberEntry || 0) * 6) - ((errorCounts.symbol || 0) * 5));
    return clampScore(
      (paragraphCompletion * 0.25) +
      (sentenceAccuracy * 0.20) +
      (accuracy * 0.20) +
      (numbersSymbols * 0.15) +
      (control * 0.20)
    );
  }

  if (lesson.module === 7) {
    const formatting = workplaceFormattingScore(errorCounts);
    const duration = lesson.minutes >= 5 ? 100 : lesson.minutes >= 4 ? 92 : lesson.minutes >= 3 ? 84 : lesson.minutes >= 2 ? 76 : 68;
    const readiness = businessReadinessScore({ accuracy, wpm, requiredSpeed, completion, errorCounts });
    return clampScore(
      (accuracy * 0.25) +
      (speedScore * 0.25) +
      (formatting * 0.20) +
      (duration * 0.10) +
      (readiness * 0.20)
    );
  }

  const accuracyWeight = isKeypadModule ? 0.60 : 0.70;
  const speedWeight = isKeypadModule ? 0.40 : 0.30;
  return Math.round((accuracy * accuracyWeight) + (speedScore * speedWeight));
}

export function formatTime(seconds) {
  if (seconds == null) return 'Untimed';
  const safe = Math.max(0, Math.ceil(seconds));
  return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, '0')}`;
}
